import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  ButtonGroup,
  Autocomplete,
} from "@mui/material";
import Swal from "sweetalert2";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/system";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../Components/helpers/Helpers";
import api from "../../api/axios";
import { useNavigate } from "react-router-dom";

const Crear = () => {
  const navigate = useNavigate();
  const { goPanel, goTable, buildTableUrl } = useLocationHelpers();

  /* =========================
     STATE
  ========================== */
  const [selectedFile, setSelectedFile] = useState(null);
  const [optionsProvinces, setOptionsProvinces] = useState([]);
  const [loadingOptionsProvinces, setLoadingOptionsProvinces] = useState(false);

  const [companyData, setCompanyData] = useState({
    companyName: "",
    companyCif: "",
    companyAddress: "",
    companyProvince: null, // ðŸ”‘ objeto
    companyPostalCode: "",
    companyContactPerson: "",
    companyEmail: "",
    companyPhone: "",
  });

  /* =========================
     FETCH DATA
  ========================== */
  useEffect(() => {
    const fetchData = async () => {
      setLoadingOptionsProvinces(true);
      try {
        const response = await api.post(
          `${process.env.REACT_APP_API_URL}/companies/create`,
        );

        setOptionsProvinces(response.data?.provinces ?? []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingOptionsProvinces(false);
      }
    };

    fetchData();
  }, []);

  /* =========================
     HANDLERS
  ========================== */
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setCompanyData((prev) => ({
      ...prev,
      [name]: value.trim(),
    }));
  }, []);

  const handleProvinceChange = (_, value) => {
    setCompanyData((prev) => ({
      ...prev,
      companyProvince: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      setSelectedFile(file);
    } else {
      Swal.fire({
        icon: "error",
        title: "Formato no permitido",
        text: "Sólo se permiten imágenes JPG o PNG",
        confirmButtonColor: "#1ab394",
      });
    }
  };

  /* =========================
     VALIDATION
  ========================== */
  const requiredFields = [
    { key: "companyName", label: "Nombre" },
    { key: "companyCif", label: "CIF" },
    { key: "companyAddress", label: "Dirección" },
    { key: "companyProvince", label: "Provincia" },
    { key: "companyPostalCode", label: "Código postal" },
    { key: "companyContactPerson", label: "Persona de contacto" },
    { key: "companyEmail", label: "Email" },
    { key: "companyPhone", label: "Teléfono" },
  ];

  const validateFields = () =>
    requiredFields.every(({ key }) => companyData[key]);

  const showValidationErrors = () => {
    const missing = requiredFields
      .filter(({ key }) => !companyData[key])
      .map(({ label }) => `<li>${label}</li>`)
      .join("");

    Swal.fire({
      icon: "error",
      title: "Campos obligatorios",
      html: `<div style="text-align:left">
        Por favor complete:
        <ul>${missing}</ul>
      </div>`,
      confirmButtonColor: "#1ab394",
    });
  };

  /* =========================
     SUBMIT
  ========================== */

  const swalConfirmCreate = () =>
    Swal.fire({
      title: "¿Crear empresa?",
      text: "Esta acción guardará la empresa en el sistema",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí­, crear",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });

  const swalSaving = () =>
    Swal.fire({
      title: "Guardando empresa",
      text: "Por favor espera...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

  const swalSuccess = () =>
    Swal.fire({
      icon: "success",
      title: "Empresa creada",
      text: "La empresa se guardó correctamente",
      confirmButtonText: "Ver empresa",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
      allowEscapeKey: false,
      allowOutsideClick: false,
    });

  const swalError = () =>
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "No se pudo guardar la empresa",
    });

  const createCompany = async (formData) => {
    const response = await api.post(
      `${process.env.REACT_APP_API_URL}/companies/store`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );

    return response.data; // aquí­ vive el id
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      showValidationErrors();
      return;
    }

    if (!selectedFile) {
      Swal.fire({
        icon: "warning",
        title: "Falta el logo",
        text: "Selecciona una imagen",
      });
      return;
    }

    const confirm = await swalConfirmCreate();
    if (!confirm.isConfirmed) return;

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("company_name", companyData.companyName);
    formData.append("company_cif", companyData.companyCif);
    formData.append("company_address", companyData.companyAddress);
    formData.append("company_province", companyData.companyProvince?.provincia);
    formData.append("company_postalcode", companyData.companyPostalCode);
    formData.append("company_contactname", companyData.companyContactPerson);
    formData.append("company_email", companyData.companyEmail);
    formData.append("company_phone", companyData.companyPhone);

    try {
      swalSaving();

      const createdCompany = await createCompany(formData);
      const companyId = createdCompany?.company_created?.id;

      Swal.close();

      const success = await swalSuccess();
      if (success.isConfirmed) {
        navigate(`/empresas/ver?id=${companyId}`);
      }
    } catch (error) {
      console.error(error, "hola");
      Swal.close();
      swalError();
    }
  };

  /* =========================
     FILE INPUT
  ========================== */
  const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    whiteSpace: "nowrap",
    width: 1,
  });

  /* =========================
     RENDER
  ========================== */
  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Empresas" subTitle="Crear" />

      <Box className="formMainContainer">
        <Grid container p={2}>
          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="Nombre"
              name="companyName"
              size="small"
              fullWidth
              value={companyData.companyName}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="CIF"
              name="companyCif"
              inputProps={{ maxLength: 10 }}
              size="small"
              fullWidth
              value={companyData.companyCif}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="Dirección"
              name="companyAddress"
              size="small"
              fullWidth
              value={companyData.companyAddress}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <Autocomplete
              value={companyData.companyProvince}
              options={optionsProvinces}
              loading={loadingOptionsProvinces}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              getOptionLabel={(o) => o?.provincia ?? ""}
              onChange={handleProvinceChange}
              renderInput={(params) => (
                <TextField {...params} label="Provincia" size="small" />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="Código postal"
              name="companyPostalCode"
              size="small"
              inputProps={{ maxLength: 10 }}
              fullWidth
              value={companyData.companyPostalCode}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="Persona de contacto"
              name="companyContactPerson"
              size="small"
              fullWidth
              value={companyData.companyContactPerson}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="Email"
              name="companyEmail"
              size="small"
              fullWidth
              value={companyData.companyEmail}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} p={1}>
            <TextField
              label="Teléfono"
              name="companyPhone"
              size="small"
              fullWidth
              value={companyData.companyPhone}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} p={1}>
            <Button component="label" startIcon={<CloudUploadIcon />}>
              Subir Logo
              <VisuallyHiddenInput type="file" onChange={handleFileChange} />
            </Button>

            {selectedFile && (
              <FormLabel sx={{ ml: 2 }}>
                Archivo seleccionado: {selectedFile.name}
              </FormLabel>
            )}
          </Grid>

          <Grid item xs={12}>
            <ButtonGroup fullWidth sx={{ mt: 2 }}>
              <Button color="success" onClick={handleSubmit}>
                Guardar
              </Button>
              <NavButton
                color="error"
                to={buildTableUrl("empresas")}
                onClick={() => goTable("empresas")}>
                Tabla
              </NavButton>
              <Button onClick={goPanel}>Inicio</Button>
            </ButtonGroup>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
};

export default Crear;
